import type React from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text, Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

import { CATEGORIES_COMPARISON_COLORS } from "../../categories-context/CategoriesContext";

interface IBaseMapTooltipProps {
    countryName: string;
}

interface ISingleMapTooltipProps extends IBaseMapTooltipProps {
    trafficShareText: string;
}

interface IIndustryData {
    name: string;
    share: string;
}
interface IComparisonMapTooltipProps extends IBaseMapTooltipProps {
    primaryIndustry: IIndustryData;
    comparedIndustry: IIndustryData;
}

const CountryNameText = ({ children }: { children: React.ReactNode }) => (
    <Text
        fontVariant="subtitle1"
        color={colorsPalettes.carbon[500]}
    >
        {children}
    </Text>
);

const BodyText = ({ children }: { children: React.ReactNode }) => (
    <Text
        data-automation="single-map-tooltio-traffic-share-value"
        fontVariant="body2"
        color={colorsPalettes.carbon[400]}
    >
        {children}
    </Text>
);

const HeaderText = ({ children }: { children: React.ReactNode }) => (
    <Text
        fontVariant="listItem3"
        color={colorsPalettes.carbon[400]}
    >
        {children}
    </Text>
);

const DataRow = ({ label, value, color }: { label: string; value: string; color: string }) => (
    <Flex
        justifyContent="space-between"
        alignItems="center"
        width="100%"
    >
        <Flex
            gap="8px"
            alignItems="center"
        >
            <Box
                background={color}
                width="8px"
                height="8px"
                borderRadius="50%"
            />
            <Text fontVariant="body2">{label}</Text>
        </Flex>
        <Text
            data-automation="map-tooltip-traffic-share-value"
            fontVariant="body2"
        >
            {value}
        </Text>
    </Flex>
);

/**
 * Single mode map tooltip component
 * Displays country name and traffic share information
 */
export const SingleMapTooltip: React.FC<ISingleMapTooltipProps> = ({ countryName, trafficShareText }) => (
    <Flex
        flexDirection="column"
        gap="16px"
        width="250px"
        padding="4px"
        data-automation="map-tooltip"
    >
        <CountryNameText>{countryName}</CountryNameText>
        <BodyText>{trafficShareText}</BodyText>
    </Flex>
);

/**
 * Comparison mode map tooltip component
 * Displays country name and comparison data between two industries/categories
 */
export const ComparisonMapTooltip: React.FC<IComparisonMapTooltipProps> = ({
    countryName,
    primaryIndustry,
    comparedIndustry,
}) => (
    <Flex
        flexDirection="column"
        gap="16px"
        width="320px"
        padding="4px"
        data-automation="map-tooltip"
    >
        <CountryNameText>{countryName}</CountryNameText>

        <Flex flexDirection="column">
            <Flex
                padding="0 0 12px 0"
                justifyContent="space-between"
                width="100%"
            >
                <HeaderText>{translate("common.tooltip.domain")}</HeaderText>
                <HeaderText>{translate("common.tooltip.trafficShare")}</HeaderText>
            </Flex>

            <Flex
                flexDirection="column"
                gap="6px"
                width="100%"
            >
                <DataRow
                    color={CATEGORIES_COMPARISON_COLORS[0]}
                    label={primaryIndustry.name}
                    value={primaryIndustry.share}
                />
                <DataRow
                    color={CATEGORIES_COMPARISON_COLORS[1]}
                    label={comparedIndustry.name}
                    value={comparedIndustry.share}
                />
            </Flex>
        </Flex>
    </Flex>
);
